import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import "./intro.css";
import darkLogo from "../../../assets/dark-logo.webp";

/* Plays once per tab: the first arrival at the site in a given tab gets the
   opening, and a return to "/" from another page, or a reload, does not. Open
   the site in a new tab or a new window and it plays again.

   This was localStorage for a while, which is per browser rather than per tab
   — so anyone who had seen the opening once never saw it again, on any visit,
   and the site simply dropped them onto the black hero. Per tab is the middle
   setting: a greeting on each visit, never twice inside one.

   public/index.html reads the same key before the bundle loads, to decide
   whether to hold a white curtain over the prerendered hero until this
   mounts. The two checks have to keep agreeing with each other.

   To watch it again: a new tab, or clear the key under Application ->
   Session Storage. */
const INTRO_KEY = "rain-intro-played";

/* Storage can be missing entirely (private windows, blocked site data). This
   flag is what keeps the curtain from running twice inside one page load when
   nothing can be written down, and it is checked first so a remount of the
   home page never replays it either. */
let playedThisLoad = false;

const flagSet = () => {
  try {
    return sessionStorage.getItem(INTRO_KEY) === "1";
  } catch {
    return false;
  }
};

const canPlay = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  if (playedThisLoad) return false;
  return !flagSet();
};

const markPlayed = () => {
  playedThisLoad = true;
  try {
    sessionStorage.setItem(INTRO_KEY, "1");
  } catch {
    /* nothing to do — playedThisLoad still covers this page load */
  }
  try {
    /* Retire the old per-browser flag on the way past. Left alone it is a
       key nothing reads, sitting in the storage of everyone who visited
       while it was the one that counted. */
    localStorage.removeItem(INTRO_KEY);
  } catch {
    /* storage blocked — there is nothing there to retire */
  }
};

/* Beat sheet, in ms from mount. A held beat of white, then the logo and the
   dot arrive together at the left and right edges and rise straight onto the
   navbar. The curtain drops as they land, so the header can assemble around
   them: the dot stretches leftwards into the Sign Up button while the nav
   links sweep in, Log In follows out of the same movement, the hero copy
   floats up as one, and the colour arrives last.

   The whole sheet was pulled 500ms earlier: the opening hold on white was the
   part that felt long, and shortening only ENTER_DELAY would have bought that
   back and then spent it again holding the logo still before the rise. Every
   beat moves together, so the choreography is unchanged — it just starts, and
   finishes, half a second sooner. */
const ENTER_DELAY = 0.5;  // seconds of plain white before anything appears
const FLY_AT = 2550;     // both rise — after a beat of holding still
const CURTAIN_AT = 3450; // backdrop clears as they land
const MORPH_AT = 3650;   // dot stretches into the button + nav sweeps in
const HANDOFF_AT = 4250; // real navbar pieces take over from the intro ones
const HERO_AT = 4450;    // headline, CTA and school logos float up together
const COLOR_AT = 5300;   // the tubes glow, last of all
const END_AT = 6800;     // overlay unmounts and scrolling is released

/* EASE_OUT is heavily front-loaded — it hits ~90% in the first third, which
   makes a move read as fast however long you set it. Good for small snappy
   bits like the button stretching; wrong for the entrance and the rise, which
   should feel unhurried. Those use the more even EASE_MOVE. */
const EASE_OUT = [0.16, 1, 0.3, 1];
const EASE_MOVE = [0.45, 0, 0.25, 1];

const IntroOverlay = ({ onFinish }) => {
  const logoRef = useRef(null);
  const dotRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [flight, setFlight] = useState(null);
  const [flying, setFlying] = useState(false);
  const [morphing, setMorphing] = useState(false);
  const [curtainOpen, setCurtainOpen] = useState(false);
  const [handedOff, setHandedOff] = useState(false);

  /* Down comes the curtain public/index.html raised before the bundle
     arrived. It is white, over the prerendered hero; this overlay's backdrop
     is the same white in the same place. Swapping them in a layout effect —
     after the overlay is in the DOM, before the browser paints — means the
     black hero underneath never gets a frame of its own. */
  useLayoutEffect(() => {
    document.documentElement.classList.remove("intro-pending");
  }, []);

  /* The navbar button's size is fixed and readable straight away — the dot
     borrows its height so the two are the same object, and its width is what
     the dot stretches out to. */
  useLayoutEffect(() => {
    const navPill = document.querySelector(".navbar .signup-btn");
    if (!navPill) return;
    const b = navPill.getBoundingClientRect();
    setTarget({ w: Math.round(b.width), h: Math.round(b.height) });
  }, []);

  /* Measure the trip at takeoff, not on mount: until the entrance settles the
     logo is still clipped to zero width, so measuring early lands it short. */
  const measureFlight = useCallback(() => {
    const navLogo = document.querySelector(".navbar .logo");
    const navPill = document.querySelector(".navbar .signup-btn");
    if (!navLogo || !logoRef.current || !dotRef.current) return null;

    const delta = (from, to, withScale) => {
      const a = from.getBoundingClientRect();
      const b = to.getBoundingClientRect();
      if (!a.height || !b.height) return { x: 0, y: 0, scale: 1 };
      return {
        x: b.left + b.width / 2 - (a.left + a.width / 2),
        y: b.top + b.height / 2 - (a.top + a.height / 2),
        scale: withScale ? b.height / a.height : 1,
      };
    };

    // The dot is already the button's height, so it only travels — and it is
    // aligned by its right edge, not its centre, because it grows leftwards
    // into the button. (The navbar's button sits shy of the padding edge: the
    // collapsed hamburger still takes a gap after it.)
    const dotRect = dotRef.current.getBoundingClientRect();
    const pillRect = navPill?.getBoundingClientRect();

    return {
      logo: delta(logoRef.current, navLogo, true),
      dot: pillRect
        ? {
            x: pillRect.right - dotRect.right,
            y: pillRect.top + pillRect.height / 2 - (dotRect.top + dotRect.height / 2),
            scale: 1,
          }
        : null,
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    /* Spend the flag at the start, not at END_AT. Navigating away mid-intro
       unmounts this and the cleanup clears every timer, so marking it at the
       end meant an interrupted run never counted — and the curtain replayed
       on the next arrival home. Starting it is what makes it played. */
    markPlayed();

    /* Freeze scrolling for the duration. Reserving the scrollbar's width as
       padding keeps the page from jumping sideways when the lock lifts. */
    const scrollbar = window.innerWidth - root.clientWidth;
    if (scrollbar > 0) root.style.setProperty("--intro-scrollbar", `${scrollbar}px`);
    root.classList.add("intro-running");

    const stage = (cls, at) => setTimeout(() => root.classList.add(cls), at);

    const timers = [
      setTimeout(() => {
        setFlight(measureFlight());
        setFlying(true);
      }, FLY_AT),
      setTimeout(() => setCurtainOpen(true), CURTAIN_AT),
      setTimeout(() => setMorphing(true), MORPH_AT),
      stage("intro-nav", MORPH_AT),
      setTimeout(() => setHandedOff(true), HANDOFF_AT),
      stage("intro-handoff", HANDOFF_AT),
      stage("intro-hero", HERO_AT),
      stage("intro-color", COLOR_AT),
      setTimeout(onFinish, END_AT),
    ];

    return () => {
      timers.forEach(clearTimeout);
      root.classList.remove(
        "intro-running",
        "intro-nav",
        "intro-handoff",
        "intro-hero",
        "intro-color"
      );
      root.style.removeProperty("--intro-scrollbar");
    };
  }, [onFinish, measureFlight]);

  const travel = (key) => {
    if (!flying || !flight?.[key]) return { x: 0, y: 0, scale: 1 };
    return flight[key];
  };

  const dotSize = target?.h ?? 37;

  return (
    <div className="intro-overlay" aria-hidden="true">
      <motion.div
        className="intro-backdrop"
        animate={{ opacity: curtainOpen ? 0 : 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />

      <motion.div
        className="intro-stage"
        animate={{ opacity: handedOff ? 0 : 1 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        {/* Entrance and flight get their own element each: both animate
            `transform`, and sharing one node makes them fight. */}
        <motion.span
          ref={logoRef}
          className="intro-fly"
          animate={travel("logo")}
          transition={{ duration: 1.05, ease: EASE_MOVE }}
        >
          <motion.img
            src={darkLogo}
            alt=""
            className="intro-logo"
            initial={{ opacity: 0, clipPath: "inset(0 100% 0 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0% 0 0)" }}
            transition={{
              opacity: { duration: 0.5, delay: ENTER_DELAY },
              clipPath: { duration: 1, delay: ENTER_DELAY, ease: EASE_MOVE },
            }}
          />
        </motion.span>

        <motion.span
          ref={dotRef}
          className="intro-fly"
          animate={travel("dot")}
          transition={{ duration: 1.05, ease: EASE_MOVE }}
        >
          {/* Starts as a dot the exact height of the navbar button, then
              stretches leftwards into it — the row is right-aligned, so
              growing the width pushes the left edge out. */}
          <motion.span
            className="intro-dot"
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{
              opacity: 1,
              scale: 1,
              width: morphing && target ? target.w : dotSize,
            }}
            style={{ height: dotSize }}
            transition={{
              opacity: { duration: 0.5, delay: ENTER_DELAY },
              scale: { duration: 0.75, delay: ENTER_DELAY, ease: EASE_MOVE },
              width: { duration: 0.5, ease: EASE_OUT },
            }}
          >
            <motion.span
              className="intro-dot-label"
              animate={{ opacity: morphing ? 1 : 0 }}
              transition={{ duration: 0.3, delay: morphing ? 0.2 : 0 }}
            >
              Sign Up
            </motion.span>
          </motion.span>
        </motion.span>
      </motion.div>
    </div>
  );
};

export { canPlay as canPlayIntro };
export default IntroOverlay;
